import React, { useState } from "react";
import {
  signInWithPopup,
  createUserWithEmailAndPassword,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { auth, provider } from "../firebase";
import { useNavigate } from "react-router-dom";

import Lottie from "lottie-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";

import animationData from "../assets/Sign up.json";
import logo from "../assets/logo.png";

import "react-toastify/dist/ReactToastify.css";
import "../styles/Register.css";

export default function Register() {
  const navigate = useNavigate();

  const [mode, setMode] = useState("google");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);

  const [loading, setLoading] = useState(false);

  // Google Register
  const handleGoogle = async () => {
    try {
      setLoading(true);
      await signInWithPopup(auth, provider);
      toast.success("Registered with Google!");
      navigate("/");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Email Register
  const handleEmail = async () => {
    if (!email || !password) return toast.error("All fields are required.");
    try {
      setLoading(true);
      await createUserWithEmailAndPassword(auth, email, password);
      toast.success("Registered successfully with Email!");
      navigate("/");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Phone Register
  const handlePhone = async () => {
    if (!phone) return toast.error("Enter phone number.");
    if (!otpSent) {
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(
          "recaptcha-container",
          { size: "invisible" },
          auth
        );
        const result = await signInWithPhoneNumber(auth, phone, window.recaptchaVerifier);
        setConfirmationResult(result);
        setOtpSent(true);
        toast.success("OTP sent!");
      } catch (err) {
        toast.error(err.message);
      }
    } else {
      try {
        await confirmationResult.confirm(otp);
        toast.success("Phone verified!");
        navigate("/");
      } catch (err) {
        toast.error("Invalid OTP");
      }
    }
  };

  const handleSubmit = () => {
    if (mode === "google") handleGoogle();
    else if (mode === "email") handleEmail();
    else if (mode === "phone") handlePhone();
  };

  return (
    <div className="register-wrapper">
      <ToastContainer />
      <div className="register-container">
        {/* Left Section */}
        <motion.div
          className="register-left"
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <img src={logo} alt="Parivar Logo" className="register-logo" />
          <h1>Join Parivar Wedding Event ✨</h1>
          <p>
            Organize events beautifully. From concept to celebration — we’re your digital partner.
          </p>
          <Lottie animationData={animationData} loop className="register-lottie" />
        </motion.div>

        {/* Right Section */}
        <motion.div
          className="register-right"
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2>Create Your Account</h2>

          {/* Auth Mode Buttons */}
          <div className="mode-buttons">
            <button className={mode === "google" ? "active" : ""} onClick={() => setMode("google")}>Google</button>
            <button className={mode === "email" ? "active" : ""} onClick={() => setMode("email")}>Email</button>
            <button className={mode === "phone" ? "active" : ""} onClick={() => { setMode("phone"); setOtpSent(false); }}>Phone</button>
          </div>

          {/* Auth Forms */}
          <AnimatePresence>
            {mode === "email" && (
              <>
                <motion.input
                  key="email"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  type="email"
                  placeholder="Enter Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <motion.div
                  className="password-container"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <input
                    type={passwordVisible ? "text" : "password"}
                    placeholder="Enter Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <span onClick={() => setPasswordVisible(!passwordVisible)}>
                    {passwordVisible ? "👁" : "🙈"}
                  </span>
                </motion.div>
              </>
            )}

            {mode === "phone" && (
              <>
                <motion.input
                  key="phone"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  type="tel"
                  placeholder="Phone (+91...)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                {otpSent && (
                  <motion.input
                    key="otp"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    type="text"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                )}
                <div id="recaptcha-container" />
              </>
            )}
          </AnimatePresence>

          {/* Action Button */}
          <button onClick={handleSubmit} className="submit-btn" disabled={loading}>
            {loading
              ? "Loading..."
              : mode === "google"
              ? "Continue with Google"
              : mode === "email"
              ? "Register"
              : otpSent
              ? "Verify OTP"
              : "Send OTP"}
          </button>

          <p className="login-text">
            Already have an account? <a href="/login">Sign in</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
